'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { ActivityLog, Category, KitchenOrder, Order, OrderItem, Product, Reservation, StationKey, Table } from '@/lib/pos-types';
import { cn } from '@/lib/utils';
import { AlertCircle, Calendar, CheckCircle2, ChefHat, Clock, Timer, Truck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

interface KitchenViewProps {
  orders: Order[];
  tables: Table[];
  products?: Product[];
  categories?: Category[];
  stationKey?: StationKey;
  activityLogs?: ActivityLog[];
  initialKitchenOrders?: KitchenOrder[];
  reservations?: Reservation[];
  onOrderStatusChange?: (orderId: string, status: Order['status']) => void;
  onOrderItemsChange?: (orderId: string, items: Order['items']) => void;
  onTicketScan?: (ticketId: string) => Promise<void>;
  onTicketReprint?: (ticketId: string) => Promise<void>;
}

const isKitchenItem = (status: Order['items'][number]['status']) =>
  status === 'sent' || status === 'in-progress' || status === 'ready'

const mapOrderToKitchenStatus = (orderStatus: Order['status'], items: OrderItem[]): KitchenOrder['status'] => {
  if (orderStatus === 'ready') {
    const hasPendingInKitchen = items.some(i => i.status === 'sent' || i.status === 'in-progress')
    return hasPendingInKitchen ? 'preparing' : 'ready'
  }
  if (orderStatus === 'in-kitchen') return 'preparing'
  return items.some(i => isKitchenItem(i.status)) ? 'preparing' : 'pending'
}

const getStationLabel = (key: StationKey) => {
  if (key === 'cocina') return 'Cocina'
  if (key === 'bar') return 'Bar'
  if (key === 'retiro') return 'Retiro'
  return 'Delivery'
}

const resolveItemStationKey = (
  item: OrderItem,
  productById: Map<string, Product>,
  categoryById: Map<string, Category>,
): StationKey => {
  const p = productById.get(item.productId) ?? null
  const productStation = (p?.stationKey ?? null) as StationKey | null
  if (productStation) return productStation
  const c = p ? (categoryById.get(p.categoryId) ?? null) : null
  const categoryStation = (c?.stationKey ?? null) as StationKey | null
  if (categoryStation) return categoryStation
  return 'cocina'
}

export function KitchenView({ orders, tables, products, categories, stationKey, activityLogs, initialKitchenOrders, reservations, onOrderStatusChange, onOrderItemsChange, onTicketScan, onTicketReprint }: KitchenViewProps) {
  const effectiveStationKey: StationKey = stationKey ?? 'cocina'
  const stationLabel = useMemo(() => getStationLabel(effectiveStationKey), [effectiveStationKey])
  const productById = useMemo(() => new Map((products ?? []).map((p) => [p.id, p] as const)), [products])
  const categoryById = useMemo(() => new Map((categories ?? []).map((c) => [c.id, c] as const)), [categories])
  const stationItemsByOrderId = useMemo(() => {
    const map = new Map<string, OrderItem[]>()
    for (const o of orders) {
      const items = o.items.filter((it) => resolveItemStationKey(it, productById, categoryById) === effectiveStationKey)
      map.set(o.id, items)
    }
    return map
  }, [categoryById, effectiveStationKey, orders, productById])

  const [scanText, setScanText] = useState('');
  const scanRef = useRef<HTMLInputElement | null>(null);
  const [expandedReadyByOrderId, setExpandedReadyByOrderId] = useState<Record<string, boolean>>({});
  const [statusByOrderId, setStatusByOrderId] = useState<Record<string, KitchenOrder['status']>>(() => {
    if (!initialKitchenOrders) return {};
    return Object.fromEntries(initialKitchenOrders.map(order => [order.orderId, order.status] as const));
  });
  const [readyEventsByOrderId, setReadyEventsByOrderId] = useState<Record<string, number[]>>({});
  const [now, setNow] = useState(() => Date.now());
  const seenOrderIdsRef = useRef<Set<string>>(new Set());
  const didInitSeenRef = useRef(false);
  const [adjustAlerts, setAdjustAlerts] = useState<{ id: string; orderId: string; tableNumber: number; createdAt: number }[]>([]);
  const seenAdjustLogIdsRef = useRef<Set<string>>(new Set());

  const adjustAlertOrderIds = useMemo(() => new Set(adjustAlerts.map((a) => a.orderId)), [adjustAlerts]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => scanRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, []);

    
  useEffect(() => {
    if (!activityLogs || activityLogs.length === 0) return

    const nowMs = Date.now()
    const windowMs = 30 * 60 * 1000
    const seen = seenAdjustLogIdsRef.current

    const newlyTriggered: {
      id: string
      orderId: string
      tableNumber: number
      createdAt: number
    }[] = []

    for (const log of activityLogs) {
      if (log.action !== 'order_adjusted') continue
      if (!log.entityId) continue
      if (!log.tableNumber) continue
      if (log.timestamp.getTime() < nowMs - windowMs) continue
      if (seen.has(log.id)) continue

      seen.add(log.id)

      newlyTriggered.push({
        id: log.id,
        orderId: log.entityId,
        tableNumber: log.tableNumber,
        createdAt: log.timestamp.getTime(),
      })
    }

    if (newlyTriggered.length === 0) return

    newlyTriggered.sort((a, b) => b.createdAt - a.createdAt)

    setAdjustAlerts((prev) => {
      const ids = new Set(prev.map((a) => a.id))

      const next = [
        ...newlyTriggered.filter((a) => !ids.has(a.id)),
        ...prev,
      ].slice(0, 8)

      return next
    })

    // AUTO REMOVE
    newlyTriggered.forEach((alert) => {
      setTimeout(() => {
        setAdjustAlerts((prev) =>
          prev.filter((x) => x.id !== alert.id)
        )
      }, 30000) // 30 segundos
    })

  }, [activityLogs])

  useEffect(() => {
    setStatusByOrderId(prev => {
      const ids = new Set(
        orders
          .filter((o) => (stationItemsByOrderId.get(o.id) ?? []).length > 0)
          .map((o) => o.id),
      );
      let changed = false;
      const next: Record<string, KitchenOrder['status']> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (!ids.has(key)) {
          changed = true;
          continue;
        }
        next[key] = value;
      }
      for (const order of orders) {
        const stationItems = stationItemsByOrderId.get(order.id) ?? []
        if (stationItems.length === 0) continue
        const baseStatus = mapOrderToKitchenStatus(order.status, stationItems);
        const override = next[order.id];
        if (override && override === baseStatus) {
          delete next[order.id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [orders, stationItemsByOrderId]);

  const kitchenOrders = useMemo(() => {
    const base: KitchenOrder[] = orders
      .map((o, index) => {
        const stationItems = stationItemsByOrderId.get(o.id) ?? []
        return { o, index, stationItems }
      })
      .filter(({ o, stationItems }) => {
        if (o.status === 'closed') return false;

        const kitchenItems = stationItems.filter((i) => isKitchenItem(i.status) || i.status === 'cancelled');
        if (kitchenItems.length === 0) return false;

        // Si es comanda de reserva sin mesa:
        if (o.reservationId && !o.tableId) {
          const res = reservations?.find((r) => r.id === o.reservationId);
          const reservedTime = res?.reservedAt ? new Date(res.reservedAt).getTime() : o.createdAt.getTime();
          const leadMins = (res?.kitchenLeadMinutes ?? 0) * 60000;
          const isDue = (reservedTime - leadMins) <= Date.now();

          // Si aun no tiene mesa asignada y no ha llegado el horario programado, se mantiene UNICAMENTE abajo en "Reservados" (sin crear Mesa 0)
          if (!isDue) return false;
        }

        return true;
      })
      .map(({ o, index, stationItems }) => {
        const kitchenItems = stationItems.filter((i) => isKitchenItem(i.status) || i.status === 'cancelled')
        return {
          id: o.id,
          orderId: o.id,
          tableNumber: o.tableNumber,
          client_name: o.clientName,
          items: kitchenItems,
          createdAt: o.createdAt,
          receivedAt: o.createdAt,
          completedAt: undefined,
          preparationTime: undefined,
          status: mapOrderToKitchenStatus(o.status, kitchenItems),
          startTime: o.sentToKitchenAt ?? o.createdAt,
          isPriority: index % 5 === 0,
          type: 'Local',
          qrCode: `CMD-${o.id}`,
          ticketPrinted: false,
        }
      })
    const withOverride = base.map(order => {
      const overrideStatus = statusByOrderId[order.id];
      return overrideStatus ? { ...order, status: overrideStatus } : order;
    });

    const computeActiveKitchenMinutes = (order: KitchenOrder) => {
      const items = order.items
      if (items.length === 0) return 0

      const roundGapMs = 60_000
      const byRound = new Map<number, typeof items>()
      const sortedByAdded = items.slice().sort((a, b) => a.addedAt.getTime() - b.addedAt.getTime())
      let round = 0
      let lastAt = sortedByAdded[0]?.addedAt.getTime() ?? 0
      for (const it of sortedByAdded) {
        const t = it.addedAt.getTime()
        if (t - lastAt > roundGapMs) round += 1
        const arr = byRound.get(round) ?? []
        arr.push(it)
        byRound.set(round, arr)
        lastAt = t
      }

      let maxMins = 0
      for (const [roundIndex, roundItems] of byRound.entries()) {
        const allReadyOrDelivered = roundItems.every((i) => i.status === 'ready' || i.status === 'delivered' || i.status === 'cancelled')
        if (allReadyOrDelivered) continue

        const minAddedAt = Math.min(...roundItems.map((i) => i.addedAt.getTime()))
        const startAtMs = roundIndex === 0 ? Math.max(order.startTime.getTime(), minAddedAt) : minAddedAt
        const mins = Math.max(0, Math.floor((now - startAtMs) / 60000))
        if (mins > maxMins) maxMins = mins
      }

      return maxMins
    }

    const sorted = withOverride
      .map((o) => ({ o, active: computeActiveKitchenMinutes(o) }))
      .sort((a, b) => {
        const byActive = b.active - a.active
        if (byActive !== 0) return byActive
        const byStart = a.o.startTime.getTime() - b.o.startTime.getTime()
        if (byStart !== 0) return byStart
        return a.o.id.localeCompare(b.o.id)
      })
      .map(({ o }, idx) => ({ ...o, isPriority: idx % 5 === 0 }))

    return sorted;
  }, [orders, statusByOrderId, now, stationItemsByOrderId]);

  const reservedKitchenOrders = useMemo(() => {
    interface ReservedOrderDisplay {
      id: string;
      clientName: string;
      partySize?: number;
      tableNumber?: number | null;
      reservedAt?: Date;
      items: Array<{
        productId: string;
        productName: string;
        quantity: number;
        notes?: string;
      }>;
    }

    const list: ReservedOrderDisplay[] = [];
    const seenIds = new Set<string>();

    for (const o of orders) {
      if (o.status === 'closed') continue;
      if (!o.reservationId) continue;
      if (o.tableId) continue;

      const res = reservations?.find((r) => r.id === o.reservationId);
      const reservedTime = res?.reservedAt ? new Date(res.reservedAt).getTime() : o.createdAt.getTime();
      const leadMins = (res?.kitchenLeadMinutes ?? 0) * 60000;
      const isDue = (reservedTime - leadMins) <= Date.now();

      // Si ya llego la hora de la reserva, se traslada a PENDIENTES arriba
      if (isDue) continue;

      const stationItems = (stationItemsByOrderId.get(o.id) ?? []).filter(
        (it) => it.status !== 'cancelled'
      );
      if (stationItems.length === 0) continue;

      seenIds.add(o.reservationId);
      seenIds.add(o.id);

      list.push({
        id: o.id,
        clientName: res?.reserverName ?? o.clientName ?? 'Cliente',
        partySize: res?.partySize,
        tableNumber: o.tableNumber ?? res?.tableNumber ?? null,
        reservedAt: res?.reservedAt ? new Date(res.reservedAt) : o.createdAt,
        items: stationItems.map((i) => ({
          productId: i.productId,
          productName: i.productName,
          quantity: i.quantity,
          notes: i.notes,
        })),
      });
    }

    if (reservations) {
      for (const r of reservations) {
        if (r.status === 'cancelled' || r.status === 'seated') continue;
        if (r.tableId) continue;
        if (seenIds.has(r.id)) continue;
        if (!r.items || r.items.length === 0) continue;

        const reservedTime = r.reservedAt ? new Date(r.reservedAt).getTime() : Date.now();
        const leadMins = (r.kitchenLeadMinutes ?? 0) * 60000;
        const isDue = (reservedTime - leadMins) <= Date.now();

        // Si ya llego la hora, pasa a pendientes arriba
        if (isDue) continue;

        const stationItems = r.items.filter(
          (it) => it.status !== 'cancelled' && resolveItemStationKey(it, productById, categoryById) === effectiveStationKey
        );
        if (stationItems.length === 0) continue;

        list.push({
          id: `res-${r.id}`,
          clientName: r.reserverName,
          partySize: r.partySize,
          tableNumber: r.tableNumber ?? null,
          reservedAt: r.reservedAt ? new Date(r.reservedAt) : undefined,
          items: stationItems.map((i) => ({
            productId: i.productId,
            productName: i.productName,
            quantity: i.quantity,
            notes: i.notes,
          })),
        });
      }
    }

    return list;
  }, [orders, reservations, stationItemsByOrderId, effectiveStationKey, productById, categoryById]);

  useEffect(() => {
    if (!didInitSeenRef.current) {
      seenOrderIdsRef.current = new Set(kitchenOrders.map((o) => o.id));
      didInitSeenRef.current = true;
      return;
    }

    const seen = seenOrderIdsRef.current;
    const newlyArrived = kitchenOrders.filter((o) => !seen.has(o.id));
    if (newlyArrived.length > 0) {
      for (const o of newlyArrived.slice(0, 3)) {
        toast.success('Nuevo pedido en cocina', { description: `Mesa ${o.tableNumber}` });
      }
      if (newlyArrived.length > 3) {
        toast.success('Nuevos pedidos en cocina', { description: `${newlyArrived.length} pedidos` });
      }
    }

    for (const o of kitchenOrders) {
      seen.add(o.id);
    }
  }, [kitchenOrders]);


  const avgPrepMinutes = useMemo(() => {
    const samples: number[] = []
    for (const o of orders) {
      const stationItems = stationItemsByOrderId.get(o.id) ?? []
      if (stationItems.length === 0) continue
      if (!o.sentToKitchenAt) continue
      if (!(o.status === 'ready' || o.status === 'delivered' || o.status === 'pending-payment' || o.status === 'closed')) continue
      if (stationItems.length === 0) continue
      const startMs = o.sentToKitchenAt.getTime()
      let endMs = 0
      for (const it of stationItems) {
        const ms = it.readyAt ? it.readyAt.getTime() : 0
        if (ms > endMs) endMs = ms
      }
      if (endMs <= 0) {
        if (o.status === 'ready' || o.status === 'delivered' || o.status === 'pending-payment' || o.status === 'closed') {
          endMs = o.updatedAt.getTime()
        }
      }
      if (endMs <= 0 || endMs < startMs) continue
      const minutes = Math.round((endMs - startMs) / 60_000)
      if (!Number.isFinite(minutes)) continue
      if (minutes <= 0 || minutes > 240) continue
      samples.push(minutes)
    }
    if (samples.length === 0) return 0
    const recent = samples.slice(-20)
    const sum = recent.reduce((acc, v) => acc + v, 0)
    return Math.max(1, Math.round(sum / recent.length))
  }, [orders, stationItemsByOrderId])

  const stats = useMemo(() => {
    return {
      pending: kitchenOrders.filter(o => o.status === 'pending').length,
      preparing: kitchenOrders.filter(o => o.status === 'preparing').length,
      ready: kitchenOrders.filter(o => o.status === 'ready').length,
      avgTime: avgPrepMinutes,
    }
  }, [avgPrepMinutes, kitchenOrders])

  const updateOrderStatus = (orderId: string, status: KitchenOrder['status']) => {
    setStatusByOrderId(prev => ({ ...prev, [orderId]: status }));
    if (status === 'ready') {
      const at = Date.now()
      setReadyEventsByOrderId((prev) => ({
        ...prev,
        [orderId]: [...(prev[orderId] ?? []), at].slice(-10),
      }))
    }
    const mappedStatus: Order['status'] =
      status === 'ready' ? 'ready' : 'in-kitchen';
    onOrderStatusChange?.(orderId, mappedStatus);
    toast.success(`Pedido marcado como ${status === 'ready' ? 'listo' : status === 'preparing' ? 'en preparación' : 'pendiente'}`);
  };

  const updatedAtByOrderId = useMemo(() => {
    const map = new Map<string, number>()
    for (const o of orders) map.set(o.id, o.updatedAt.getTime())
    return map
  }, [orders])

  const fullOrderById = useMemo(() => {
    const map = new Map<string, Order>()
    for (const o of orders) map.set(o.id, o)
    return map
  }, [orders])

  const readErrorMessage = (err: unknown): string => {
    const isRecord = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object'
    const e = isRecord(err) ? err : {}
    const response = isRecord(e.response) ? e.response : null
    const data = response && isRecord(response.data) ? response.data : null
    const msg = data && typeof data.message === 'string' ? data.message : null
    if (msg && msg.trim().length > 0) return msg
    const status = response && typeof response.status === 'number' ? response.status : null
    if (typeof status === 'number') return `Error HTTP ${status}`
    return 'Error desconocido'
  }

  const extractTicketId = (raw: string): string | null => {
    const s = raw.trim()
    if (!s) return null
    const lower = s.toLowerCase()
    const m1 = lower.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)
    if (m1?.[0]) return m1[0]
    const m2 = lower.match(/([0-9a-f]{8})[^0-9a-f]*([0-9a-f]{4})[^0-9a-f]*([0-9a-f]{4})[^0-9a-f]*([0-9a-f]{4})[^0-9a-f]*([0-9a-f]{12})/)
    if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}-${m2[4]}-${m2[5]}`
    const m3 = lower.match(/[0-9a-f]{32}/)
    if (m3?.[0]) {
      const hex = m3[0]
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
    }
    return null
  }

  const normalizeTicketScan = (raw: string): string | null => {
    const s = raw.trim()
    if (!s) return null
    const extracted = extractTicketId(s)
    if (extracted) return extracted
    if (s.toUpperCase().includes('TICKET')) {
      const after = s.replace(/pos[^0-9a-f]*ticket[^0-9a-f]*/i, '')
      const extractedAfter = extractTicketId(after)
      if (extractedAfter) return extractedAfter
    }
    return null
  }

  const activeKitchenOrders = useMemo(() => kitchenOrders.filter((o) => o.status !== 'ready'), [kitchenOrders])
  const readyKitchenOrders = useMemo(() => kitchenOrders.filter((o) => o.status === 'ready'), [kitchenOrders])

  const   renderKitchenCard = (order: KitchenOrder, opts?: { compact?: boolean }) => {
    const originalOrder = orders.find(o => o.id === order.orderId);
    const table = tables.find(t => t.id === originalOrder?.tableId) || tables.find(t => t.number === order.tableNumber);
    const openedAtRaw = table?.openedAt ?? order.createdAt;
    const openedAt = order.startTime && openedAtRaw.getTime() > order.startTime.getTime() ? order.startTime : openedAtRaw;
    const tableMinutes = Math.floor((now - openedAt.getTime()) / 60000);
    const derivedReadyEvents = order.items
      .map((i) => i.readyAt?.getTime() ?? null)
      .filter((t): t is number => typeof t === 'number' && Number.isFinite(t))
    const readyEvents = Array.from(new Set([...(readyEventsByOrderId[order.id] ?? []), ...derivedReadyEvents])).sort((a, b) => a - b)
    const lastReadyAt =
      readyEvents.length > 0 ? readyEvents[readyEvents.length - 1] : order.status === 'ready' ? (updatedAtByOrderId.get(order.id) ?? 0) : 0
    const roundGapMs = 60_000

    const roundPalettes = [
      { text: 'text-sky-400', bg: 'bg-sky-500/15', ring: 'ring-1 ring-sky-500/30', badge: 'bg-sky-500/10 text-sky-400 border-sky-500/30' },
      { text: 'text-emerald-400', bg: 'bg-emerald-500/15', ring: 'ring-1 ring-emerald-500/30', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
      { text: 'text-amber-400', bg: 'bg-amber-500/15', ring: 'ring-1 ring-amber-500/30', badge: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
      { text: 'text-violet-400', bg: 'bg-violet-500/15', ring: 'ring-1 ring-violet-500/30', badge: 'bg-violet-500/10 text-violet-400 border-violet-500/30' },
      { text: 'text-pink-400', bg: 'bg-pink-500/15', ring: 'ring-1 ring-pink-500/30', badge: 'bg-pink-500/10 text-pink-400 border-pink-500/30' },
    ] as const

    const itemsSorted = order.items
      .slice()
      .sort((a, b) => {
        const aCancelled = a.status === 'cancelled'
        const bCancelled = b.status === 'cancelled'
        if (aCancelled !== bCancelled) return aCancelled ? -1 : 1
        const aReady = a.status === 'ready'
        const bReady = b.status === 'ready'
        if (aReady !== bReady) return aReady ? 1 : -1
        const byAdded = a.addedAt.getTime() - b.addedAt.getTime()
        if (byAdded !== 0) return byAdded
        return a.id.localeCompare(b.id)
      })

    const roundIndexByItemId = (() => {
      const sortedByAdded = order.items.slice().sort((a, b) => a.addedAt.getTime() - b.addedAt.getTime())
      const map = new Map<string, number>()
      let round = 0
      let lastAt = sortedByAdded[0]?.addedAt.getTime() ?? 0
      for (const it of sortedByAdded) {
        const t = it.addedAt.getTime()
        if (t - lastAt > roundGapMs) round += 1
        map.set(it.id, round)
        lastAt = t
      }
      return map
    })()

    const itemsByRoundIndex = (() => {
      const map = new Map<number, typeof itemsSorted>()
      for (const it of itemsSorted) {
        const idx = roundIndexByItemId.get(it.id) ?? 0
        const arr = map.get(idx) ?? []
        arr.push(it)
        map.set(idx, arr)
      }
      return map
    })()

    const roundSummaries = Array.from(itemsByRoundIndex.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([roundIndex, roundItems]) => {
        const palette = roundPalettes[roundIndex % roundPalettes.length]
        const minAddedAt = Math.min(...roundItems.map((i) => i.addedAt.getTime()))
        const startAtMs = roundIndex === 0 ? Math.max(order.startTime.getTime(), minAddedAt) : minAddedAt

        const updatedAtFallback = updatedAtByOrderId.get(order.id) ?? 0
        const allReadyOrDelivered = roundItems.every((i) => i.status === 'ready' || i.status === 'delivered' || i.status === 'cancelled')
        const allDelivered = roundItems.every((i) => i.status === 'delivered' || i.status === 'cancelled')

        const readyAtMs = allReadyOrDelivered
          ? Math.max(
              ...roundItems.map((i) => i.readyAt?.getTime() ?? (i.status === 'ready' || i.status === 'delivered' || i.status === 'cancelled' ? updatedAtFallback : 0)),
            )
          : null

        const deliveredAtMs = allDelivered
          ? Math.max(
              ...roundItems.map((i) => i.deliveredAt?.getTime() ?? (i.status === 'delivered' || i.status === 'cancelled' ? updatedAtFallback : 0)),
            )
          : null

        const kitchenEndMs = readyAtMs ?? now
        const kitchenMins = Math.max(0, Math.floor((kitchenEndMs - startAtMs) / 60000))

        const deliveryMins =
          readyAtMs === null
            ? null
            : Math.max(0, Math.floor(((deliveredAtMs ?? now) - readyAtMs) / 60000))

        return {
          sendIndex: roundIndex,
          palette,
          kitchenMins,
          kitchenRunning: readyAtMs === null,
          deliveryMins,
          deliveryRunning: readyAtMs !== null && deliveredAtMs === null,
          allDelivered,
          itemIds: roundItems.map((i) => i.id),
        }
      })

    const activeRound = (() => {
      const running = roundSummaries.filter((r) => r.kitchenRunning)
      if (running.length === 0) return null
      return running.reduce((best, cur) => (cur.kitchenMins > best.kitchenMins ? cur : best), running[0])
    })()

    /* Marcar como listo */
    const markRoundReady = (roundIndex: number) => {
      const full = fullOrderById.get(order.id)
      if (!full) return
      const roundItems = itemsByRoundIndex.get(roundIndex) ?? []
      if (roundItems.length === 0) return

      const ids = new Set(roundItems.map((i) => i.id))
      const readyAt = new Date()
      const nextItems = full.items.map((i) => {
        if (!ids.has(i.id)) return i
        if (i.status === 'delivered' || i.status === 'cancelled') return i
        return { ...i, status: 'ready' as const, readyAt: i.readyAt ?? readyAt }
      })

      onOrderItemsChange?.(order.id, nextItems)
      toast.success(`Envío ${roundIndex + 1} listo`, { description: `Mesa ${order.tableNumber}` })

      const allReadyOrDelivered = nextItems.every((i) => i.status === 'ready' || i.status === 'delivered' || i.status === 'cancelled')
      if (allReadyOrDelivered) {
        updateOrderStatus(order.id, 'ready')
      }
    }

    const activeKitchenMinutes = (() => {
      const running = roundSummaries.filter((r) => r.kitchenRunning)
      if (running.length === 0) return 0
      return Math.max(...running.map((r) => r.kitchenMins))
    })()

    const activeDeliveryMinutes = (() => {
      const running = roundSummaries.filter((r) => r.deliveryMins !== null && r.deliveryRunning)
      if (running.length > 0) return Math.max(...running.map((r) => r.deliveryMins ?? 0))
      const any = roundSummaries.filter((r) => r.deliveryMins !== null)
      if (any.length > 0) return Math.max(...any.map((r) => r.deliveryMins ?? 0))
      return 0
    })()

    const isUrgent = activeKitchenMinutes > 15 && order.status !== 'ready'
    const isAdjusted = adjustAlertOrderIds.has(order.id)
    const isReadyCollapsed = (opts?.compact ?? false) ? true : (order.status === 'ready' && !expandedReadyByOrderId[order.id])

    return (
      <Card
        key={order.id}
        className={cn(
          'bg-card border-2 flex flex-col overflow-hidden transition-all duration-300 min-h-0',
          isReadyCollapsed ? 'h-[110px] sm:h-[120px]' : 'min-h-[420px] sm:min-h-[460px] xl:min-h-[520px] max-h-[520px] sm:max-h-[620px]',
          isAdjusted ? 'border-fuchsia-500 shadow-lg shadow-fuchsia-500/10' : isUrgent ? 'border-red-500 shadow-lg shadow-red-500/10' : 'border-border',
          order.status === 'ready' && 'opacity-70 grayscale-[0.2]'
        )}
      >
        <div
          className={cn(
            'p-3 sm:p-4 border-b border-border flex items-center justify-between',
            isAdjusted ? 'bg-fuchsia-500/10' : isUrgent ? 'bg-red-500/10' : 'bg-muted/30',
          )}
          role={!opts?.compact && order.status === 'ready' ? 'button' : undefined}
          tabIndex={!opts?.compact && order.status === 'ready' ? 0 : undefined}
          onClick={() => {
            if (opts?.compact) return
            if (order.status !== 'ready') return
            setExpandedReadyByOrderId((prev) => ({ ...prev, [order.id]: !prev[order.id] }))
          }}
          onKeyDown={(e) => {
            if (opts?.compact) return
            e.preventDefault()
            setExpandedReadyByOrderId((prev) => ({ ...prev, [order.id]: !prev[order.id] }))
          }}
        >
          <div className="flex flex-col min-w-0">
            <h4 className="text-base sm:text-lg font-black text-foreground flex items-center gap-2 truncate">
              {order.client_name
                ? order.client_name
                : order.tableNumber
                ? "Mesa " + order.tableNumber
                : "Para llevar"}
             
              {order.isPriority && (
                <Badge variant="destructive" className="animate-pulse text-[8px] h-4">PRIORIDAD</Badge>
              )}
              {isAdjusted && (
                <Badge variant="outline" className="text-[8px] h-4 border-fuchsia-500/40 text-fuchsia-500 bg-fuchsia-500/10 animate-pulse">
                  AJUSTE ADMIN
                </Badge>
              )}
            </h4>
            {!order.client_name && table?.zone && (
              <span className="text-xs text-muted-foreground font-normal leading-none mt-0.5 mb-1">
                {table.zone}
              </span>
            )}
            <p className="text-[10px] sm:text-xs text-muted-foreground font-mono">#{order.id.slice(-6).toUpperCase()}</p>
          </div>
          <div className="text-right">
            <div className={cn('text-[10px] sm:text-xs font-black flex items-center justify-end gap-1.5', isUrgent ? 'text-red-500' : 'text-foreground')}>
              <Clock className="w-3.5 h-3.5" />
              Mesa {tableMinutes}m
            </div>
            <div className={cn('text-[10px] sm:text-xs font-black flex items-center justify-end gap-1.5', isUrgent ? 'text-red-500' : 'text-foreground')}>
              <Timer className="w-3.5 h-3.5" />
              {stationLabel} {activeKitchenMinutes}m
            </div>
            <div className={cn('text-[10px] sm:text-xs font-black flex items-center justify-end gap-1.5', isUrgent ? 'text-red-500' : 'text-foreground')}>
              <Truck className="w-3.5 h-3.5" />
              Entrega {activeDeliveryMinutes}m
            </div>
            <p className="text-[9px] text-muted-foreground uppercase font-bold">{order.type}</p>
          </div>
        </div>

        {!isReadyCollapsed && (
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 pb-6">
              
              {itemsSorted.map((item) => {
                const isReady = item.status === 'ready'
                const isCancelled = item.status === 'cancelled'
                const itemAt = item.addedAt.getTime()
                const sendIndex = roundIndexByItemId.get(item.id) ?? 0
                const palette = roundPalettes[sendIndex % roundPalettes.length]
                const isNewRound = !isReady && lastReadyAt > 0 && itemAt > lastReadyAt
                return (
                  <div key={item.id} className="flex gap-3">
                    <div
                      className={cn(
                        'w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center font-bold text-xs sm:text-sm shrink-0',
                        isCancelled
                          ? 'bg-red-500/15 text-red-500 ring-1 ring-red-500/30'
                          : isReady
                          ? 'bg-muted/40 text-muted-foreground'
                          : isNewRound
                            ? `${palette.bg} ${palette.text} ${palette.ring}`
                            : `${palette.bg} ${palette.text} ${palette.ring}`,
                      )}
                    >
                      {item.quantity}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        'text-xs sm:text-sm font-bold leading-tight',
                        isCancelled
                          ? 'text-red-500 line-through'
                          : isReady
                            ? 'text-muted-foreground line-through'
                            : `${palette.text}`,
                        item.notes && "mb-1"
                      )}>
                        {isCancelled ? `ANULAR: ${item.productName}` : item.productName}
                      </p>
                      {item.notes && (
                        <div className="mt-1.5 p-1.5 rounded bg-amber-500/10 border border-amber-500/20">
                          <p className="text-[10px] text-amber-600 font-bold italic flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {item.notes}
                          </p>
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-1.5">
                        <Badge variant="outline" className={cn('text-[9px]', palette.badge)}>
                          ENVÍO {sendIndex + 1}
                        </Badge>
                        {isNewRound && (
                          <Badge variant="outline" className={cn('text-[9px]', palette.badge)}>
                            NUEVO
                          </Badge>
                        )}
                        {isCancelled && (
                          <Badge variant="outline" className="text-[9px] border-red-500/40 text-red-500 bg-red-500/10">
                            ANULAR
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        )}

        {!isReadyCollapsed && (
          <div className="p-3 sm:p-4 border-t border-border mt-auto bg-muted/20 flex gap-2">
            {order.status === 'pending' && activeRound !== null && (
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold h-10 sm:h-11 shadow-md shadow-amber-500/20"
                onClick={() => updateOrderStatus(order.id, 'preparing')}
              >
                PREPARAR
              </Button>
            )}
            {order.status === 'pending' && activeRound === null && (
              <div className="flex w-full">
                <div className="flex-1 py-3 text-center text-[11px] font-bold text-muted-foreground bg-muted/50 rounded-md border border-border">
                  ESPERANDO A OTRAS ÁREAS
                </div>
              </div>
            )}
            {order.status === 'preparing' && activeRound !== null && (
              <div className="flex w-full gap-2">
                <Button
                  variant="outline"
                  className="w-1/3 text-[10px] h-10 sm:h-11 font-bold"
                  onClick={() => updateOrderStatus(order.id, 'pending')}
                >
                  PAUSAR
                </Button>
                <Button
                  className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-black h-10 sm:h-11 shadow-md shadow-primary/20"
                  onClick={() => {
                    markRoundReady(activeRound.sendIndex)
                  }}
                >
                  LISTO ENVÍO
                </Button>
              </div>
            )}
            {order.status === 'preparing' && activeRound === null && (
              <div className="flex w-full">
                <div className="flex-1 py-3 text-center text-[11px] font-bold text-muted-foreground bg-muted/50 rounded-md border border-border">
                  ESPERANDO A OTRAS ÁREAS
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  const renderReservedKitchenCard = (r: {
    id: string;
    clientName: string;
    partySize?: number;
    tableNumber?: number | null;
    reservedAt?: Date;
    items: Array<{
      productId: string;
      productName: string;
      quantity: number;
      notes?: string;
    }>;
  }) => {
    return (
      <Card
        key={`res-card-${r.id}`}
        className="w-[280px] sm:w-[320px] shrink-0 bg-card border-amber-500/30 shadow-lg flex flex-col justify-between"
      >
        <div className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2 border-b border-border/60 pb-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-black text-sm text-foreground truncate">
                  Reserva: {r.clientName}
                </span>
                <Badge variant="secondary" className="text-[10px] bg-amber-500/20 text-amber-400 border-amber-500/30 font-bold shrink-0">
                  {r.partySize ? `${r.partySize} pers` : "Reserva"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 font-medium">
                <span className="flex items-center gap-1 text-amber-400 font-bold shrink-0">
                  <Clock className="w-3 h-3" />
                  {r.reservedAt ? r.reservedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                </span>
                <span>·</span>
                <span className="truncate">{r.tableNumber ? `Mesa ${r.tableNumber}` : "Sin Mesa"}</span>
              </div>
            </div>
            <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 font-black text-[10px] uppercase shrink-0">
              RESERVADO
            </Badge>
          </div>

          <div className="space-y-1 py-1 max-h-[120px] overflow-y-auto pr-1">
            {r.items.map((it, idx) => (
              <div key={`res-item-${r.id}-${idx}`} className="flex items-center justify-between text-xs">
                <span className="font-semibold text-foreground truncate max-w-[200px]">
                  <span className="font-black text-amber-400 mr-1.5">{it.quantity}x</span>
                  {it.productName}
                </span>
                {it.notes && (
                  <span className="text-[10px] text-muted-foreground italic truncate max-w-[90px]" title={it.notes}>
                    {it.notes}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="px-3 py-1.5 bg-amber-500/10 border-t border-amber-500/20 text-[10px] text-amber-300 font-medium flex items-center justify-between">
          <span>{r.items.length} plato(s) reservados</span>
          <span className="font-bold">Control Preventivo</span>
        </div>
      </Card>
    );
  };

  return (
    <div className="flex flex-col h-full gap-4 sm:gap-6">
      {adjustAlerts.length > 0 && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[min(560px,calc(100vw-1rem))]">
          <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/10 backdrop-blur px-3 py-2 shadow-lg shadow-fuchsia-500/10">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <AlertCircle className="w-4 h-4 text-fuchsia-500 shrink-0" />
                <span className="text-xs sm:text-sm font-black text-fuchsia-500 truncate">AJUSTE ADMIN</span>
              </div>
              <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] font-bold" onClick={() => setAdjustAlerts([])}>
                Limpiar
              </Button>
            </div>
            <div className="mt-2 space-y-1">
              {adjustAlerts.slice(0, 4).map((a) => (
                
                <div key={a.id} className="flex items-center justify-between gap-3">
                  <span className="text-[11px] font-bold text-foreground">Mesa {a.tableNumber}</span>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-7 px-2 text-[10px] font-bold"
                    onClick={() => setAdjustAlerts((prev) => prev.filter((x) => x.id !== a.id))}
                  >
                    Ok
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1">
        <div className="flex items-center gap-2">
          <ChefHat className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-black tracking-tight text-foreground">{stationLabel}</h2>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={scanRef}
            value={scanText}
            onChange={(e) => setScanText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const raw = scanText.trim();
              if (!raw) return;
              const ticketId = normalizeTicketScan(raw);
              if (!ticketId) {
                toast.error('QR inválido');
                return;
              }
              setScanText('');
              void (async () => {
                try {
                  if (!onTicketScan) throw new Error('scan-disabled');
                  await onTicketScan(ticketId);
                  toast.success('Ticket marcado como listo');
                } catch (err) {
                  toast.error('No se pudo procesar el ticket', { description: readErrorMessage(err) });
                } finally {
                  scanRef.current?.focus();
                }
              })();
            }}
            className="h-9 w-full sm:w-[280px] rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            placeholder="Escanear QR (POS-TICKET:...)"
          />
          <Button variant="outline" size="sm" className="font-bold" onClick={() => scanRef.current?.focus()}>Escanear</Button>
          <Button
            size="sm"
            className="font-bold"
            variant="secondary"
            onClick={() => {
              const raw = scanText.trim();
              if (!raw) return;
              const ticketId = normalizeTicketScan(raw);
              if (!ticketId) {
                toast.error('QR inválido');
                return;
              }
              setScanText('');
              void (async () => {
                try {
                  if (!onTicketReprint) throw new Error('reprint-disabled');
                  await onTicketReprint(ticketId);
                  toast.success('Ticket reimpreso');
                } catch (err) {
                  toast.error('No se pudo reimprimir', { description: readErrorMessage(err) });
                } finally {
                  scanRef.current?.focus();
                }
              })();
            }}
          >
            Reimprimir
          </Button>
        </div>
      </div>
      {/* Stats Header - Responsive */}
      <div className="flex gap-2 sm:gap-4 overflow-x-auto no-scrollbar pb-1 px-1">
        <Card className="bg-card border-border shrink-0 sm:flex-1 min-w-[100px] sm:min-w-[120px]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase font-bold tracking-wider">Pendientes</p>
              <p className="text-lg sm:text-2xl font-black text-foreground">{stats.pending}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border shrink-0 sm:flex-1 min-w-[100px] sm:min-w-[120px]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
              <Timer className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase font-bold tracking-wider">En prep.</p>
              <p className="text-lg sm:text-2xl font-black text-foreground">{stats.preparing}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border shrink-0 sm:flex-1 min-w-[100px] sm:min-w-[120px]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <ChefHat className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase font-bold tracking-wider">Entregados</p>
              <p className="text-lg sm:text-2xl font-black text-foreground">{stats.ready}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border shrink-0 sm:flex-1 min-w-[100px] sm:min-w-[120px]">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase font-bold tracking-wider">T. Prom.</p>
              <p className="text-lg sm:text-2xl font-black text-foreground">{stats.avgTime}m</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders Grid - Responsive Columns */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full pr-1">
          <div className="space-y-4 pb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {activeKitchenOrders.map((o) => renderKitchenCard(o))}
            </div>

            {reservedKitchenOrders.length > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 overflow-hidden">
                <div className="w-full px-3 py-2 flex items-center justify-between bg-amber-500/10">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-black text-amber-500">Reservados</span>
                    <Badge variant="outline" className="font-mono text-[10px] bg-amber-500/20 text-amber-400 border-amber-500/30">
                      {reservedKitchenOrders.length}
                    </Badge>
                  </div>
                  <span className="text-[10px] text-amber-400/80 font-bold">Control Preventivo / Sin Mesa</span>
                </div>

                <div className="p-3">
                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                    {reservedKitchenOrders.map((r) => renderReservedKitchenCard(r))}
                  </div>
                </div>
              </div>
            )}

            {readyKitchenOrders.length > 0 && (
              <div className="rounded-xl border border-border bg-card/40 overflow-hidden">
                <div className="w-full px-3 py-2 flex items-center justify-between bg-muted/30">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-foreground">Listos</span>
                    <Badge variant="outline" className="font-mono text-[10px]">{readyKitchenOrders.length}</Badge>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-bold">Visible</span>
                </div>

                <div className="p-3">
                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                    {readyKitchenOrders.map((o) => renderKitchenCard(o, { compact: true }))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
